import { useState, useEffect, useCallback } from 'react';
import { Phone, MapPin, Clock, User, ArrowRight, Bell, X, Loader2, FileText, Info, ChevronLeft, ChevronRight } from 'lucide-react';
import QualificationLeadSortant from './QualificationLeadSortant';
import QualificationAppel from './QualificationAppel';
import getApiUrl from '../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


export default function BanniereLeadsLive({ authUser, onAppelerLead, onOpenClientFiche }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);
  const [assigningLead, setAssigningLead] = useState(null);
  
  // État pour le modal de qualification
  const [showQualification, setShowQualification] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  
  // État pour le modal QualificationAppel (après Décroché)
  const [showQualificationAppel, setShowQualificationAppel] = useState(false);
  const [prefillLeadData, setPrefillLeadData] = useState(null);

  // Charger les nouveaux leads (avec détails complets pour Clubtravaux)
  const loadLeads = useCallback(async () => {
    try {
      const resp = await authFetch(`${API}/webhook/leads/nouveaux?limit=10`);
      if (resp.ok) {
        const data = await resp.json();
        // Charger les détails complets pour chaque lead
        const leadsWithDetails = await Promise.all(
          (data.leads || []).map(async (lead) => {
            try {
              const detailResp = await authFetch(`${API}/webhook/leads/${lead.lead_id}`);
              if (detailResp.ok) {
                return await detailResp.json();
              }
            } catch (e) {
              console.error('Erreur chargement détails lead:', e);
            }
            return lead;
          })
        );
        setLeads(leadsWithDetails);
      }
    } catch (e) {
      console.error('Erreur chargement leads:', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadLeads();
    // Rafraîchir toutes les 10 secondes
    const interval = setInterval(loadLeads, 10000);
    return () => clearInterval(interval);
  }, [loadLeads]);

  // Navigation manuelle - DOIT être déclaré AVANT le useEffect qui l'utilise
  const [isManualNavigation, setIsManualNavigation] = useState(false);

  // Défilement automatique
  useEffect(() => {
    if (leads.length <= 1) return;
    if (isManualNavigation) return; // Pas d'auto-scroll si navigation manuelle
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % leads.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [leads.length, isManualNavigation]);
  
  const nextLead = () => {
    setIsManualNavigation(true);
    setCurrentIndex(prev => (prev + 1) % leads.length);
    // Reprendre l'auto-scroll après 10 secondes
    setTimeout(() => setIsManualNavigation(false), 10000);
  };
  
  const prevLead = () => {
    setIsManualNavigation(true);
    setCurrentIndex(prev => (prev - 1 + leads.length) % leads.length);
    // Reprendre l'auto-scroll après 10 secondes
    setTimeout(() => setIsManualNavigation(false), 10000);
  };

  // Assigner et appeler un lead - Ouvre la popup de qualification
  const handleAppeler = async (lead) => {
    console.log('handleAppeler appelé avec lead:', lead);
    console.log('authUser:', authUser);
    
    if (!authUser?.email) {
      alert('Vous devez être connecté');
      return;
    }
    
    setAssigningLead(lead.lead_id);
    
    try {
      // Assigner le lead au télépro
      const url = `${API}/webhook/leads/${lead.lead_id}/assigner?telepro_email=${encodeURIComponent(authUser.email)}&telepro_nom=${encodeURIComponent(authUser.name || authUser.email)}`;
      console.log('Assignation URL:', url);
      
      const resp = await authFetch(url, {
        method: 'POST'
      });
      
      console.log('Réponse assignation:', resp.status, resp.ok);
      
      if (resp.ok) {
        console.log('Assignation OK, ouverture du modal...');
        // Ouvrir le modal de qualification avec les infos du lead
        setSelectedLead(lead);
        setShowQualification(true);
        console.log('Modal state set: showQualification=true, selectedLead=', lead);
        
        // NE PAS retirer le lead de la bannière ici - on le retire seulement après qualification
        // Le lead reste visible mais on peut le gérer via le modal
      } else {
        const errorText = await resp.text();
        console.error('Erreur assignation:', errorText);
        alert('Erreur lors de l\'assignation: ' + errorText);
      }
    } catch (e) {
      console.error('Erreur assignation:', e);
      alert('Erreur lors de l\'assignation du lead');
    }
    
    setAssigningLead(null);
  };
  
  // Callback quand le lead est mis à jour via le modal
  const handleLeadUpdated = (updatedLead) => {
    // Ne retirer le lead de la bannière QUE si c'est une qualification définitive
    // (pas pour les NRP simples qui gardent le lead dans la bannière)
    const statutsDefinitifs = ['qualifie', 'rdv_pris', 'injoignable', 'a_rappeler', 'hors_cible'];
    
    if (updatedLead?.lead_id && statutsDefinitifs.includes(updatedLead.statut)) {
      setLeads(prev => prev.filter(l => l.lead_id !== updatedLead.lead_id));
    }
    
    // Optionnel: callback vers le parent
    if (onAppelerLead) {
      onAppelerLead(updatedLead);
    }
    // Rafraîchir la liste des leads pour avoir les données à jour
    loadLeads();
  };

  // Callback quand le prospect décroche → basculer vers QualificationAppel
  const handleDecroche = (lead, selectedTelepro) => {
    // Fermer la modale Appel Sortant
    setShowQualification(false);
    setSelectedLead(null);
    // Ouvrir QualificationAppel avec les données pré-remplies
    setPrefillLeadData({
      ...lead,
      selectedTelepro,
    });
    setShowQualificationAppel(true);
  };

  // Format date
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    return date.toLocaleDateString('fr-FR');
  };

  // Pas de leads
  if (!loading && leads.length === 0) {
    return null; // Ne rien afficher si pas de leads
  }

  // Minimisé
  if (isMinimized) {
    return (
      <button
        onClick={() => setIsMinimized(false)}
        className="fixed bottom-4 right-4 z-50 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-3 rounded-full shadow-lg flex items-center gap-2 animate-pulse hover:animate-none hover:scale-105 transition-transform"
      >
        <Bell className="w-5 h-5" />
        <span className="font-bold">{leads.length} nouveau{leads.length > 1 ? 'x' : ''} lead{leads.length > 1 ? 's' : ''}</span>
      </button>
    );
  }

  const currentLead = leads[currentIndex];

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50">
      {/* Bannière principale */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-t border-emerald-500/30 shadow-2xl">
        {/* Header avec compteur */}
        <div className="flex items-center justify-between px-4 py-2 bg-emerald-500/10 border-b border-emerald-500/20">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-emerald-400 rounded-full animate-pulse" />
              <span className="text-emerald-400 font-bold text-sm uppercase tracking-wider">
                Nouveaux Leads
              </span>
            </div>
            <span className="bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
              {leads.length}
            </span>
          </div>
          
          {/* Indicateurs de défilement */}
          {leads.length > 1 && (
            <div className="flex gap-1">
              {leads.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentIndex ? 'bg-emerald-400 w-6' : 'bg-slate-600 hover:bg-slate-500'
                  }`}
                />
              ))}
            </div>
          )}
          
          <button
            onClick={() => setIsMinimized(true)}
            className="p-1 hover:bg-slate-700 rounded"
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        {/* Contenu du lead */}
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
          </div>
        ) : currentLead ? (
          <div className="px-6 py-4">
            <div className="flex items-center justify-between max-w-6xl mx-auto">
              {/* Flèche gauche */}
              {leads.length > 1 && (
                <button 
                  onClick={prevLead}
                  className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors mr-4"
                  title="Lead précédent"
                >
                  <ChevronLeft className="w-5 h-5 text-slate-400" />
                </button>
              )}
              
              {/* Infos lead */}
              <div className="flex items-center gap-6 flex-1">
                {/* Avatar */}
                <div className="relative">
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg">
                    {currentLead.prenom?.[0] || currentLead.nom?.[0] || '?'}
                  </div>
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-emerald-400 rounded-full flex items-center justify-center animate-bounce">
                    <Bell className="w-3 h-3 text-white" />
                  </div>
                </div>
                
                {/* Nom */}
                <div>
                  <p className="text-white text-xl font-bold">
                    {currentLead.prenom} {currentLead.nom}
                  </p>
                  <p className="text-slate-400 text-sm flex items-center gap-2">
                    <span className="text-emerald-400">{currentLead.source}</span>
                    <span>•</span>
                    <Clock className="w-3 h-3" />
                    {formatTime(currentLead.date_creation)}
                  </p>
                </div>
                
                {/* Téléphone */}
                <div className="flex items-center gap-2 bg-slate-800 px-4 py-2 rounded-xl flex-shrink-0">
                  <Phone className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <span className="text-white font-mono text-lg tracking-wider whitespace-nowrap">
                    {currentLead.telephone?.replace(/(\d{2})(?=\d)/g, '$1 ')}
                  </span>
                </div>
                
                {/* Département */}
                <div className="flex items-center gap-2 bg-blue-500/20 px-4 py-2 rounded-xl border border-blue-500/30">
                  <MapPin className="w-5 h-5 text-blue-400" />
                  <span className="text-blue-400 font-bold text-lg">
                    DPT {currentLead.departement}
                  </span>
                </div>
                
                {/* Infos Clubtravaux si présentes */}
                {currentLead.projet_externe_id && (
                  <div className="flex items-center gap-2 bg-amber-500/20 px-3 py-2 rounded-xl border border-amber-500/30">
                    <FileText className="w-4 h-4 text-amber-400" />
                    <span className="text-amber-400 font-medium text-sm">
                      {currentLead.projet_externe_id}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Infos complémentaires Clubtravaux (si présentes) */}
              {currentLead.infos_complementaires && (
                <div className="hidden xl:flex items-center gap-2 bg-slate-800/80 px-4 py-2 rounded-lg max-w-md">
                  <Info className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <span className="text-slate-300 text-sm truncate" title={currentLead.infos_complementaires}>
                    {currentLead.infos_complementaires}
                  </span>
                </div>
              )}
              
              {/* Bouton Appeler */}
              <button
                onClick={() => handleAppeler(currentLead)}
                disabled={assigningLead === currentLead.lead_id}
                data-testid="appeler-lead-btn"
                className="flex items-center gap-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-lg shadow-emerald-500/30 transition-all hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
              >
                {assigningLead === currentLead.lead_id ? (
                  <>
                    <Loader2 className="w-6 h-6 animate-spin" />
                    Attribution...
                  </>
                ) : (
                  <>
                    <Phone className="w-6 h-6" />
                    APPELER
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
              
              {/* Flèche droite */}
              {leads.length > 1 && (
                <button 
                  onClick={nextLead}
                  className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors ml-4"
                  title="Lead suivant"
                >
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </button>
              )}
            </div>
          </div>
        ) : null}
      </div>
      
      {/* Modal de qualification - Appel Sortant (résultat appel) */}
      <QualificationLeadSortant
        isOpen={showQualification}
        onClose={() => {
          setShowQualification(false);
          setSelectedLead(null);
        }}
        lead={selectedLead}
        authUser={authUser}
        onLeadUpdated={handleLeadUpdated}
        onDecroche={handleDecroche}
      />
      
      {/* Modal de qualification - Flow complet (après Décroché) */}
      <QualificationAppel
        isOpen={showQualificationAppel}
        onClose={() => {
          setShowQualificationAppel(false);
          setPrefillLeadData(null);
          loadLeads();
        }}
        authUser={authUser}
        onLeadCreated={(newLead) => {
          handleLeadUpdated(newLead);
          setShowQualificationAppel(false);
          setPrefillLeadData(null);
        }}
        onOpenClientFiche={onOpenClientFiche}
        prefillLead={prefillLeadData}
      />
    </div>
  );
}
